import React from 'react';
import { LabelPill } from '@src/components/LabelPill';
import { useAtomValue, useSetAtom } from 'jotai';
import { getFilter } from '@src/pages/Discover/components/Selectors/Selectors';
import { FilterState, InitialLibraryState } from '@src/pages/Discover/components/Atoms/Atoms';
import { onFilterChange } from '@src/fragments/Filters/utils/FilterStateUtility';
import { FilterSelections } from '@src/fragments/Filters/FilterSelections';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@src/components/ActionButton';
import { PerfectScrollbar } from '@src/components/PerfectScrollbar';
import { style } from 'typestyle';

interface AppliedFiltersProps {
  matchType: 'partial' | 'exact';
  onMatchTypeChange: (newMatchType: 'partial' | 'exact') => void;
}

export function AppliedFilters({ matchType, onMatchTypeChange }: AppliedFiltersProps) {
  const filterState = useAtomValue(getFilter);
  const setFilterState = useSetAtom(FilterState);
  const libraryState = useAtomValue(InitialLibraryState);

  
  // Functions to remove specific filters
  const removeLabelFilter = (labelToRemove: string) => {
    onFilterChange(setFilterState, 'label')([labelToRemove], false);
  };
  
  const removeHashtagFilter = (hashtagToRemove: string) => {
    onFilterChange(setFilterState, 'hashtag')([hashtagToRemove], false);
  };
  
  const removeCaptionFilter = (captionToRemove: string) => {
    onFilterChange(setFilterState, 'caption')([captionToRemove], false);
  };

  return (
    <div className={appliedFiltersClass}>
      <div className={headerClass}>
        <div className={filtersTitleClass}>
          Applied Filters
        </div>
        
        <div className={buttonGroupClass}>
          <Tooltip 
            title={window.T('collect.collectoverlay.create.auto_collection.partial')}
            arrow
            placement="top"
          >
            <span>
              <ActionButton
                variant={matchType === 'partial' ? 'blue' : 'transparent'}
                size="small"
                minWidth={70}
                onAction={() => onMatchTypeChange('partial')}
              >
                Partial (<strong>{libraryState.totalEstimated}</strong>)
              </ActionButton>
            </span>
          </Tooltip>
          <Tooltip 
            title={window.T('collect.collectoverlay.create.auto_collection.exact')}
            arrow
            placement="top"
          >
            <span>
              <ActionButton
                variant={matchType === 'exact' ? 'blue' : 'transparent'}
                size="small"
                minWidth={70}
                onAction={() => onMatchTypeChange('exact')}
              >
                Exact (<strong>{libraryState.totalExact}</strong>)
              </ActionButton>
            </span>
          </Tooltip>
        </div>
      </div>
      
      <PerfectScrollbar className={scrollableContentClass}>
        {/* Labels Section */}
        {filterState.label && filterState.label.length > 0 && (
          <div className={filterSectionClass}>
            <span className={filterLabelClass}>Labels:</span>
            <div className={filterPillsClass}>
              {filterState.label.map((label, index) => (
                <LabelPill 
                  key={`label-${label}-${index}`}
                  onRemove={() => removeLabelFilter(label)}
                >
                  <span className={pillTextClass}>{label}</span>
                </LabelPill>
              ))}
            </div>
          </div>
        )}
      
        {/* Hashtags Section */}
        {filterState.hashtag && filterState.hashtag.length > 0 && (
          <div className={filterSectionClass}>
            <span className={filterLabelClass}>Hashtags:</span>
            <div className={filterPillsClass}>
              {filterState.hashtag.map((hashtag, index) => (
                <LabelPill 
                  key={`hashtag-${hashtag}-${index}`}
                  onRemove={() => removeHashtagFilter(hashtag)}
                >
                  <span className={pillTextClass}>#{hashtag}</span>
                </LabelPill>
              ))}
            </div>
          </div>
        )}
        {filterState.caption && filterState.caption.length > 0 && (
          <div className={filterSectionClass}>
            <span className={filterLabelClass}>Caption:</span>
            <div className={filterPillsClass}>
              {filterState.caption.map((caption, index) => (
                <LabelPill 
                  key={`caption-${caption}-${index}`}
                  onRemove={() => removeCaptionFilter(caption)}
                >
                  <span className={pillTextClass}>"{ caption}"</span>
                </LabelPill>
              ))}
            </div>
          </div>
        )}

        <hr className={dividerClass} />
        <div className={otherFiltersClass}>
          <FilterSelections />
        </div>
      </PerfectScrollbar>
    </div>
  );
}


// Concise local styles
const appliedFiltersClass = style({ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.04)' });
const headerClass = style({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 });
const filtersTitleClass = style({ fontSize: 14, fontWeight: 500 });
const buttonGroupClass = style({ display: 'flex', gap: 6 });
const scrollableContentClass = style({ flex: 1 });
const filterSectionClass = style({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 6 });
const filterLabelClass = style({ fontSize: 13, fontWeight: 500 });
const filterPillsClass = style({ display: 'flex', flexWrap: 'wrap', gap: 4 });
const pillTextClass = style({ fontSize: 12 });
const dividerClass = style({ borderWidth: 0.3, margin: '6px 0' });
const otherFiltersClass = style({ fontSize: 13 });
